import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { api } from "../lib/api";

const FLAGS = {
  it: "🇮🇹", en: "🇬🇧", fr: "🇫🇷", es: "🇪🇸", ro: "🇷🇴", ar: "🇸🇦",
};
const NAMES = {
  it: "Italiano", en: "English", fr: "Français", es: "Español", ro: "Română", ar: "العربية",
};

export function LanguageSwitcher({ compact = false, variant = "light" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const change = async (code) => {
    try {
      await i18n.changeLanguage(code);
      // Save server-side if logged in
      try { await api.patch("/me/locale", { locale: code }); } catch (_) { /* not logged in */ }
    } finally {
      setOpen(false);
    }
  };
  const cur = i18n.language || "it";
  const isDark = variant === "dark";
  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="lang-switcher-trigger"
        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full transition-all text-xs font-semibold"
        style={{
          background: isDark ? "rgba(15,23,42,0.05)" : "rgba(255,255,255,0.10)",
          border: isDark ? "1px solid rgba(15,23,42,0.10)" : "1px solid rgba(255,255,255,0.20)",
          color: isDark ? "#0F172A" : "#FFFFFF",
          backdropFilter: "blur(10px)",
        }}
        aria-label="Cambia lingua"
      >
        <Globe className="w-3.5 h-3.5" />
        {!compact && <span>{FLAGS[cur]} {cur.toUpperCase()}</span>}
        {compact && <span>{FLAGS[cur]}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-44 rounded-2xl overflow-hidden z-50"
             style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.10)", boxShadow: "0 20px 50px -15px rgba(15,23,42,0.20)" }}
             data-testid="lang-switcher-panel">
          {Object.keys(FLAGS).map(code => (
            <button
              key={code}
              onClick={() => change(code)}
              data-testid={`lang-option-${code}`}
              className="w-full px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-slate-50 transition-colors text-left"
              style={{ color: "#0F172A" }}
            >
              <span className="text-base">{FLAGS[code]}</span>
              <span className="flex-1 font-semibold">{NAMES[code]}</span>
              {cur === code && <Check className="w-4 h-4 text-emerald-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
